import type { ReportBlockFieldVM } from '@acceligentllc/shared/dtos/socket/view/newFieldReport/base';

import type { AddressFieldVM, EquipmentFieldVM, SignatureFieldVM, ValueType } from '@acceligentllc/shared/utils/fieldReport';
import { isValidNumber } from '@acceligentllc/shared/utils/validation';

import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';

export const getFormName = (id: string, index?: Nullable<number>): string => {
	return isValidNumber(index) ? `field-${id}[${index}]` : `field-${id}`;
};

const isNullOrEmptyString = (value: unknown): boolean => {
	return value === null || value === undefined || value === '';
};

const isEffectivelyEmptyArray = (
	arr: unknown[],
	isEmptyValue: (value: unknown) => boolean = isNullOrEmptyString
): boolean => {
	if (arr.length === 0) {
		return true;
	}
	return arr.every(isEmptyValue);
};

const isCompoundUnitValueEmpty = (value: unknown): boolean => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return true;
	}
	const keys = Object.keys(value);
	return keys.length > 0 && keys.every((key) => !value[key]);
};

const isAddressValueEmpty = (value: unknown): boolean => {
	const address = value as AddressFieldVM; // Assuming an AddressFieldVM type
	if (typeof address !== 'object' || address === null) {
		return true; // Treat non-objects as empty
	}
	// An address is empty if all its relevant properties are empty
	return isNullOrEmptyString(address.street) &&
		isNullOrEmptyString(address.streetNumber) &&
		isNullOrEmptyString(address.locality) &&
		isNullOrEmptyString(address.postalCode) &&
		isNullOrEmptyString(address.country);
};

export const fieldReportFieldHasValue = (value: ValueType, fieldDefinition: ReportBlockFieldVM): boolean => {
	const ignoredTypes = [
		ReportBlockFieldEnum.Type.CALCULATED,
		ReportBlockFieldEnum.Type.IMMUTABLE_TEXT,
		ReportBlockFieldEnum.Type.IMMUTABLE_IMAGE,
	];

	if (ignoredTypes.includes(fieldDefinition.fieldType)) {
		return false;
	}

	let isEmpty = false;

	switch (fieldDefinition.fieldType) {
		case ReportBlockFieldEnum.Type.ADDRESS:
			isEmpty = Array.isArray(value)
				? isEffectivelyEmptyArray(value, isAddressValueEmpty)
				: isAddressValueEmpty(value);
			break;

		case ReportBlockFieldEnum.Type.SIGNATURE:
			isEmpty = Array.isArray(value)
				? isEffectivelyEmptyArray(value, (v) => isNullOrEmptyString(v) || (v as SignatureFieldVM)?.id === -1)
				: (value as SignatureFieldVM)?.id === -1 || isNullOrEmptyString(value);
			break;

		case ReportBlockFieldEnum.Type.EQUIPMENT:
			isEmpty = Array.isArray(value)
				? isEffectivelyEmptyArray(value, (v) => (v as EquipmentFieldVM)?.id === null)
				: (value as EquipmentFieldVM)?.id === null;
			break;

		case ReportBlockFieldEnum.Type.BOOLEAN:
			isEmpty = Array.isArray(value)
				? isEffectivelyEmptyArray(value, (v) => v === false)
				: value === false;
			break;

		default:
			// Handle all other types (TEXT, NUMBER, etc.) and special cases like compound units
			if (fieldDefinition.unit?.includes('_')) { // Compound unit
				isEmpty = Array.isArray(value)
					? isEffectivelyEmptyArray(value, isCompoundUnitValueEmpty)
					: isCompoundUnitValueEmpty(value);
			} else {
				isEmpty = Array.isArray(value)
					? isEffectivelyEmptyArray(value, isNullOrEmptyString)
					: isNullOrEmptyString(value);
			}
			break;
	}

	return !isEmpty;
};
